<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import portalMixin from '../mixins/portalMixin';
import articleAPI from 'dashboard/api/helpCenter/articles';

export default {
  components: {
    Thumbnail,
  },
  mixins: [portalMixin],
  props: {
    portal: {
      type: Object,
      default: () => ({}),
    },
    active: {
      type: Boolean,
      default: false,
    },
    activePortalSlug: {
      type: String,
      default: '',
    },
    activeLocale: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedLocale: null,
      isNavigating: false,
    };
  },
  computed: {
    locales() {
      return this.portal?.config?.allowed_locales;
    },
    currentArticle() {
      if (this.$route.name !== 'edit_article') return null;
      const id = this.$route.params.articleSlug;
      if (!id) return null;
      return this.$store.getters['articles/articleById'](id);
    },
    articlesCount() {
      const { allowed_locales: allowedLocales } = this.portal.config;
      return allowedLocales.reduce((acc, locale) => {
        return acc + locale.articles_count;
      }, 0);
    },
  },
  mounted() {
    this.selectedLocale = this.locale || this.portal?.meta?.default_locale;
  },
  methods: {
    async onLocaleClick(code, portal) {
      if (this.isNavigating) return; // guard against double clicks
      this.isNavigating = true;
      try {
        if (this.$route.name === 'edit_article') {
          // Ensure we have the article in store (user might click before load completes)
          let current = this.currentArticle;
          const routeArticleId = this.$route.params.articleSlug;
          if (!current && routeArticleId) {
            try {
              await this.$store.dispatch('articles/show', {
                id: routeArticleId,
                portalSlug: this.$route.params.portalSlug,
              });
              current = this.currentArticle;
            } catch (_) {
              // ignore fetch error; we'll fallback
            }
          }

          // Derive root associated id (if current is a child uses associated_article_id, if root it uses its own id)
          const baseAssociatedId = current
            ? current.associated_article_id || current.id
            : routeArticleId;

          if (baseAssociatedId) {
            try {
              const response = await articleAPI.getArticleByAssociatedArticleId({
                portalSlug: portal.slug,
                associatedArticleId: baseAssociatedId,
                locale: code,
              }
              );
              const targetArticle = response?.data?.payload;
              if (targetArticle?.id) {
                this.$router.push({
                  name: 'edit_article',
                  params: {
                    portalSlug: portal.slug,
                    locale: code,
                    articleSlug: targetArticle.id,
                  },
                });
                this.$emit('fetchPortal');
                this.$emit('openPortalPage');
                return;
              }
            } catch (_) {
              // ignore lookup errors -> fallback below
            }
          }
        }

        // Fallback: open locale root article list
        this.$router.push({
          name: 'list_all_locale_articles',
          params: {
            portalSlug: portal.slug,
            locale: code,
          },
        });
        this.$emit('fetchPortal');
        this.$emit('openPortalPage');
      } finally {
        this.isNavigating = false;
      }
    },
    isLocaleActive(code, slug) {
      const isPortalActive = this.portal.slug === slug;
      const isLocaleActive = this.activeLocale === code;
      return isPortalActive && isLocaleActive;
    },
    isLocaleDefault(code) {
      return this.portal?.meta?.default_locale === code;
    },
  },
};
</script>

<template>
  <div class="portal" :class="{ active }">
    <Thumbnail :username="portal.name" variant="square" />
    <div class="actions-container">
      <header class="flex items-center justify-between mb-2.5">
        <div>
          <h3 class="text-sm mb-0.5 text-slate-700 dark:text-slate-100">
            {{ portal.name }}
          </h3>
          <p class="mb-0 text-xs text-slate-600 dark:text-slate-200">
            {{ articlesCount }}
            {{ $t('HELP_CENTER.PORTAL.ARTICLES_LABEL') }}
          </p>
        </div>
        <woot-label
          v-if="active"
          variant="smooth"
          size="small"
          color-scheme="success"
          :title="$t('HELP_CENTER.PORTAL.ACTIVE_BADGE')"
        />
      </header>
      <div class="portal-locales">
        <h5 class="text-base text-slate-700 dark:text-slate-100">
          {{ $t('HELP_CENTER.PORTAL.CHOOSE_LOCALE_LABEL') }}
        </h5>
        <ul>
          <li v-for="locale in locales" :key="locale.code">
            <woot-button
              :variant="`locale-item ${
                isLocaleActive(locale.code, activePortalSlug)
                  ? 'smooth'
                  : 'clear'
              }`"
              size="large"
              color-scheme="secondary"
              type="button"
              :is-loading="isNavigating"
              @click="onLocaleClick(locale.code, portal)"
            >
              <div class="flex items-center justify-between w-full">
                <div class="meta">
                  <h6 class="text-sm text-left mb-0.5">
                    <span class="text-slate-700 dark:text-slate-100">
                      {{ localeName(locale.code) }}
                    </span>
                    <span
                      v-if="isLocaleDefault(locale.code)"
                      class="text-sm text-slate-300 dark:text-slate-200"
                    >
                      {{ `(${$t('HELP_CENTER.PORTAL.DEFAULT')})` }}
                    </span>
                  </h6>

                  <span
                    class="flex w-full text-sm leading-4 text-left text-slate-600 dark:text-slate-200"
                  >
                    {{ locale.articles_count }}
                    {{ $t('HELP_CENTER.PORTAL.ARTICLES_LABEL') }} -
                    {{ locale.code }}
                  </span>
                </div>
                <div v-if="isLocaleActive(locale.code, activePortalSlug)">
                  <fluent-icon icon="checkmark" class="locale__radio" />
                </div>
              </div>
            </woot-button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.portal {
  @apply bg-white dark:bg-slate-800 rounded-md p-4 relative flex mb-4 border border-solid border-slate-100 dark:border-slate-600;

  &.active {
    @apply bg-white dark:bg-slate-800 border border-solid border-woot-400 dark:border-woot-500;
  }

  .actions-container {
    @apply ml-2.5 rtl:ml-0 rtl:mr-2.5 flex-grow;

    .portal-locales {
      ul {
        @apply list-none p-0 m-0;
      }

      .locale__radio {
        @apply w-8 text-green-600 dark:text-green-600;
      }
    }
  }

  .locale-item {
    @apply flex items-start py-1 px-4 rounded-md w-full mb-2;

    p {
      @apply mb-0 text-left;
    }
  }
}
</style>
